import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Camera, Upload, Trash2 } from "lucide-react";
import { api } from "../api";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";

/**
 * Worker photo viewer + uploader.
 *
 * Two modes:
 *  - <WorkerPhotoModal /> — controlled modal that displays the photo full-size.
 *    Used in Kedatangan when a worker name is clicked.
 *  - <WorkerPhotoUploader /> — inline uploader used in worker details / staff
 *    photo-management pages. Auto-resizes to JPEG ~500KB before upload.
 */

// Resize an image File to <=maxW × maxH, JPEG, quality 0.7 — produces a Blob
// that's ~80–95% smaller than the source for typical phone photos.
async function resizeImage(file, maxW = 1024, maxH = 1024, quality = 0.7) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Cannot read file"));
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Cannot decode image"));
    i.src = dataUrl;
  });
  const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality)
  );
  return new File([blob], "worker-photo.jpg", { type: "image/jpeg" });
}

function buildPhotoUrl(workerId, bust) {
  return `/roster/workers/${workerId}/photo${bust ? `?v=${bust}` : ""}`;
}

// Fetches the photo via axios (so the JWT bearer token is sent) and returns
// an object-URL the <img> tag can render. Caller is responsible for calling
// URL.revokeObjectURL on the returned URL when no longer needed.
async function fetchPhotoObjectUrl(workerId, bust) {
  const res = await api.get(buildPhotoUrl(workerId, bust), { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

export function WorkerPhotoModal({ open, onOpenChange, worker, onChanged }) {
  const { user } = useAuth();
  // Upload: ANY signed-in staff who can see this modal (i.e. has roster
  // access to the worker's company). Backend re-checks the assertion.
  const canUpload = !!user;
  // Delete: ADMIN ONLY — frontline staff who upload a wrong photo can
  // self-correct by re-uploading; only admin can hard-remove the photo
  // after verification.
  const canDelete = !!user && user.role === "admin";
  const [bust, setBust] = useState(0);
  const [busy, setBusy] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [objectUrl, setObjectUrl] = useState("");

  // Fetch the photo whenever the modal opens or photo changes (bust).
  // Must use an authenticated axios call — direct <img src> won't send the JWT.
  useEffect(() => {
    if (!open || !worker?.id || !worker?.has_photo) {
      setObjectUrl("");
      return;
    }
    let cancelled = false;
    let url = "";
    setImgError(false);
    fetchPhotoObjectUrl(worker.id, bust)
      .then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        url = u;
        setObjectUrl(u);
      })
      .catch(() => { if (!cancelled) setImgError(true); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, worker?.id, worker?.has_photo, bust]);

  useEffect(() => {
    if (open) { setBust(Date.now()); setImgError(false); }
  }, [open, worker?.id]);

  if (!worker) return null;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    setBusy(true);
    try {
      const resized = await resizeImage(f);
      const fd = new FormData();
      fd.append("file", resized);
      await api.post(`/roster/workers/${worker.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      setBust(Date.now());
      setImgError(false);
      onChanged?.();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Remove photo for ${worker.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/roster/workers/${worker.id}/photo`);
      toast.success("Photo removed");
      setBust(Date.now());
      setImgError(true);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const hasPhoto = !!worker.has_photo && !imgError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md p-0 overflow-hidden"
        data-testid="worker-photo-modal">
        <DialogHeader className="px-5 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-blue-400" />
            <span className="truncate">{worker.name}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Reference photo for worker {worker.name}.
            {canDelete
              ? " You can upload, change, or delete the photo."
              : (canUpload ? " You can upload or replace the photo." : " Read-only view.")}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-3">
          <div className="flex items-center gap-2 text-[11px] text-slate-400 num">
            {worker.code && <span className="px-1.5 py-0.5 rounded bg-slate-800/60 border border-slate-700">#{worker.code}</span>}
            {worker.company_name && <span className="truncate">{worker.company_name}</span>}
          </div>
        </div>

        <div className="bg-black flex items-center justify-center min-h-[260px] max-h-[60vh]">
          {hasPhoto && objectUrl ? (
            <img
              src={objectUrl}
              alt={worker.name}
              className="max-w-full max-h-[60vh] object-contain"
              onError={() => setImgError(true)}
              data-testid="worker-photo-img"
            />
          ) : (
            <div className="text-center px-6 py-10 text-slate-500">
              <Camera className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <div className="text-sm">No photo on file yet.</div>
              {!canUpload && (
                <div className="text-[11px] mt-1">
                  Ask an admin to add one.
                </div>
              )}
            </div>
          )}
        </div>

        {canUpload ? (
          <div className="flex items-center gap-2 px-5 py-3 border-t hairline bg-[#0a0e1a]">
            <label className="flex-1">
              <input type="file" accept="image/*" className="hidden"
                onChange={onFile} disabled={busy}
                data-testid="worker-photo-upload-input" />
              <span className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-md
                bg-blue-600 hover:bg-blue-500 text-white text-sm cursor-pointer transition-colors">
                <Upload className="w-3.5 h-3.5" />
                {busy ? "Uploading…" : (hasPhoto ? "Replace photo" : "Upload photo")}
              </span>
            </label>
            {hasPhoto && canDelete && (
              <Button variant="ghost" size="sm"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={onDelete} disabled={busy}
                title="Delete photo (admin only)"
                data-testid="worker-photo-delete">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ) : (
          <div className="px-5 py-2 text-[11px] text-slate-500 border-t hairline bg-[#0a0e1a]">
            Click anywhere outside to close
          </div>
        )}
        {hasPhoto && !canDelete && (
          <div className="px-5 pb-3 -mt-1 text-[10px] text-slate-500 italic">
            Wrong photo? Click <b>Replace photo</b> above to upload a new one.
            Only admin can delete.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
